<template>
    <div class="portal-card-wrapper portal-card-wrapper--large">
        <div class="portal-card portal-card--plain">
            <div class="portal-card__plain-header">
                <h2 class="card-main-title">
                    {{ $t('pages.public.record_upload.step_account.title') }}
                </h2>
                <p class="card-main-subtitle">
                    {{ $t('pages.public.record_upload.step_account.subtitle') }}
                </p>
            </div>

            <div v-if="accountSubStep === 'options'" class="portal-card__body">
                <div class="account-options-grid">
                    <button type="button" :class="guestOptionCardClass" @click="selectAccountMode('guest')">
                        <div class="option-icon-box">
                            <common-icon name="user" size="medium"></common-icon>
                        </div>
                        <div class="option-content">
                            <p class="option-title">
                                {{ $t('pages.public.record_upload.step_account.guest_title') }}
                            </p>
                            <p class="option-desc">
                                {{ $t('pages.public.record_upload.step_account.guest_desc') }}
                            </p>
                        </div>
                    </button>

                    <button type="button" :class="createOptionCardClass" @click="selectAccountMode('create')">
                        <div class="recommended-badge">
                            {{ $t('pages.public.record_upload.step_account.recommended_badge') }}
                        </div>
                        <div class="option-icon-box option-icon-box--blue">
                            <common-icon name="add-user" size="medium"></common-icon>
                        </div>
                        <div class="option-content">
                            <p class="option-title option-title--blue">
                                {{ $t('pages.public.record_upload.step_account.create_account_title') }}
                            </p>
                            <p class="option-desc option-desc--blue">
                                {{ $t('pages.public.record_upload.step_account.create_account_desc') }}
                            </p>
                        </div>
                    </button>

                    <button type="button" :class="signinOptionCardClass" @click="selectAccountMode('signin')">
                        <div class="option-icon-box">
                            <common-icon name="lock" size="medium"></common-icon>
                        </div>
                        <div class="option-content">
                            <p class="option-title">
                                {{ $t('pages.public.record_upload.step_account.signin_title') }}
                            </p>
                            <p class="option-desc">
                                {{ $t('pages.public.record_upload.step_account.signin_desc') }}
                            </p>
                        </div>
                    </button>
                </div>
            </div>

            <div v-else-if="accountSubStep === 'create_form'" class="portal-card__body">
                <div class="create-account-form">
                    <div class="form-grid-2x2">
                        <div class="form-group-step">
                            <label class="form-label-small">
                                {{ $t('pages.public.record_upload.step_account.full_name_label') }}
                            </label>
                            <input
                                v-model="accountForm.fullName"
                                type="text"
                                class="input-box"
                                :placeholder="$t('pages.public.record_upload.step_account.full_name_placeholder')"
                            />
                        </div>
                        <div class="form-group-step">
                            <label class="form-label-small">
                                {{ $t('pages.public.record_upload.step_account.org_label') }}
                            </label>
                            <input
                                v-model="accountForm.organization"
                                type="text"
                                class="input-box"
                                :placeholder="$t('pages.public.record_upload.step_account.org_placeholder')"
                            />
                        </div>
                        <div class="form-group-step">
                            <label class="form-label-small">
                                {{ $t('pages.public.record_upload.step_account.work_email_label') }}
                            </label>
                            <input
                                v-model="accountForm.email"
                                type="email"
                                class="input-box"
                                :placeholder="$t('pages.public.record_upload.step_account.email_placeholder')"
                            />
                        </div>
                        <div class="form-group-step">
                            <label class="form-label-small">
                                {{ $t('pages.public.record_upload.step_account.password_label') }}
                            </label>
                            <input
                                v-model="accountForm.password"
                                type="password"
                                class="input-box"
                                :placeholder="$t('pages.public.record_upload.step_account.password_placeholder')"
                            />
                        </div>
                    </div>

                    <p class="terms-text">
                        {{ $t('pages.public.record_upload.step_account.terms_prefix') }}
                        <a href="#">{{ $t('pages.public.record_upload.step_account.terms_of_service') }}</a>
                        {{ $t('pages.public.record_upload.step_account.and') }}
                        <a href="#">{{ $t('pages.public.record_upload.step_account.privacy_policy') }}</a>
                        .
                    </p>

                    <div>
                        <a href="#" class="back-to-options-link" @click.prevent="backToOptions">
                            <common-icon name="left-arrow" size="extra-small"></common-icon>
                            <span>{{ $t('pages.public.record_upload.step_account.back_to_options') }}</span>
                        </a>
                    </div>
                </div>
            </div>

            <div v-else-if="accountSubStep === 'signin_form'" class="portal-card__body">
                <div class="signin-account-form">
                    <div class="form-group-step">
                        <label class="form-label-small">
                            {{ $t('pages.public.record_upload.step_account.email_label') }}
                        </label>
                        <input
                            v-model="signInForm.email"
                            type="email"
                            class="input-box"
                            :placeholder="$t('pages.public.record_upload.step_account.email_placeholder')"
                        />
                    </div>
                    <div class="form-group-step">
                        <label class="form-label-small">
                            {{ $t('pages.public.record_upload.step_account.password_label') }}
                        </label>
                        <input
                            v-model="signInForm.password"
                            type="password"
                            class="input-box"
                            :placeholder="$t('pages.public.record_upload.step_account.signin_password_placeholder')"
                        />
                        <p class="forgot-password-row">
                            <a href="#" class="forgot-password-link" @click.prevent="handleForgotPassword">
                                {{ $t('pages.public.record_upload.step_account.forgot_password') }}
                            </a>
                        </p>
                    </div>

                    <div>
                        <a href="#" class="back-to-options-link" @click.prevent="backToOptions">
                            <common-icon name="left-arrow" size="extra-small"></common-icon>
                            <span>{{ $t('pages.public.record_upload.step_account.back_to_options') }}</span>
                        </a>
                    </div>
                </div>
            </div>

            <div v-else-if="accountSubStep === 'guest_form'" class="portal-card__body">
                <div class="guest-account-form">
                    <div class="guest-info-banner">
                        <common-icon name="user" size="small"></common-icon>
                        <p class="guest-info-text">
                            {{ $t('pages.public.record_upload.step_account.guest_info_prefix') }}
                            <strong>{{ $t('pages.public.record_upload.step_account.guest_highlight') }}</strong>
                            .
                            {{ $t('pages.public.record_upload.step_account.guest_info_suffix') }}
                        </p>
                    </div>

                    <div class="form-group-step">
                        <label class="form-label-small">
                            {{ $t('pages.public.record_upload.step_account.guest_email_label') }}
                        </label>
                        <input
                            v-model="guestEmail"
                            type="email"
                            class="input-box"
                            :placeholder="$t('pages.public.record_upload.step_account.email_placeholder')"
                        />
                    </div>

                    <div>
                        <a href="#" class="back-to-options-link" @click.prevent="backToOptions">
                            <common-icon name="left-arrow" size="extra-small"></common-icon>
                            <span>{{ $t('pages.public.record_upload.step_account.back_to_options') }}</span>
                        </a>
                    </div>
                </div>
            </div>
        </div>

        <div class="step-nav-bar">
            <button type="button" class="nav-btn-back" @click="$emit('back')">
                <common-icon name="left-arrow" size="small"></common-icon>
                <span>{{ $t('common.microcopy.back') }}</span>
            </button>

            <button type="button" :class="primaryButtonClass" :disabled="isPrimaryButtonDisabled" @click="handlePrimaryAction">
                <span>{{ primaryButtonLabel }}</span>
                <common-icon v-if="showPrimaryActionArrow" name="right-arrow" size="small"></common-icon>
            </button>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'step-account',
        props: {
            initialEmail: { type: String, default: '' },
        },
        data() {
            return {
                accountSubStep: 'options', // 'options' | 'guest_form' | 'create_form' | 'signin_form'
                selectedAccountMode: null, // 'guest' | 'create' | 'signin'
                guestEmail: this.initialEmail || '',
                signInForm: {
                    email: this.initialEmail || '',
                    password: '',
                },
                accountForm: {
                    fullName: '',
                    organization: '',
                    email: this.initialEmail || '',
                    password: '',
                },
            }
        },
        computed: {
            guestOptionCardClass() {
                return {
                    'account-option-card': true,
                    'account-option-card--selected': this.selectedAccountMode === 'guest',
                }
            },
            createOptionCardClass() {
                return {
                    'account-option-card': true,
                    'account-option-card--recommended': true,
                    'account-option-card--selected': this.selectedAccountMode === 'create',
                }
            },
            signinOptionCardClass() {
                return {
                    'account-option-card': true,
                    'account-option-card--selected': this.selectedAccountMode === 'signin',
                }
            },
            isPrimaryButtonDisabled() {
                if (this.accountSubStep === 'options') {
                    return !this.selectedAccountMode
                }
                if (this.accountSubStep === 'guest_form') {
                    return !this.guestEmail || !this.guestEmail.trim()
                }
                if (this.accountSubStep === 'create_form') {
                    return !this.accountForm.fullName || !this.accountForm.email || !this.accountForm.password
                }
                if (this.accountSubStep === 'signin_form') {
                    return !this.signInForm.email || !this.signInForm.password
                }
                return false
            },
            primaryButtonLabel() {
                if (this.accountSubStep === 'options') {
                    return this.selectedAccountMode
                        ? this.$t('common.microcopy.continue')
                        : this.$t('pages.public.record_upload.step_account.select_option_continue')
                }
                if (this.accountSubStep === 'guest_form') {
                    return this.$t('pages.public.record_upload.step_account.guest_title')
                }
                if (this.accountSubStep === 'create_form') {
                    return this.$t('pages.public.record_upload.step_account.create_and_continue')
                }
                if (this.accountSubStep === 'signin_form') {
                    return this.$t('pages.public.record_upload.step_account.signin_and_continue')
                }
                return this.$t('common.microcopy.continue')
            },
            showPrimaryActionArrow() {
                if (this.accountSubStep === 'options') {
                    return !!this.selectedAccountMode
                }
                return true
            },
            primaryButtonClass() {
                return {
                    'nav-btn-action': true,
                    'nav-btn-action--active': !this.isPrimaryButtonDisabled,
                    'nav-btn-action--disabled': this.isPrimaryButtonDisabled,
                }
            },
        },
        methods: {
            selectAccountMode(mode) {
                this.selectedAccountMode = mode
                if (mode === 'guest') {
                    this.accountSubStep = 'guest_form'
                } else if (mode === 'create') {
                    this.accountSubStep = 'create_form'
                } else if (mode === 'signin') {
                    this.accountSubStep = 'signin_form'
                }
            },
            backToOptions() {
                this.accountSubStep = 'options'
                this.selectedAccountMode = null
            },
            handlePrimaryAction() {
                if (this.isPrimaryButtonDisabled) return

                if (this.accountSubStep === 'options') {
                    if (this.selectedAccountMode === 'create') {
                        this.accountSubStep = 'create_form'
                    } else if (this.selectedAccountMode === 'signin') {
                        this.accountSubStep = 'signin_form'
                    } else if (this.selectedAccountMode === 'guest') {
                        this.accountSubStep = 'guest_form'
                    }
                } else if (this.accountSubStep === 'guest_form') {
                    this.$emit('continue', { mode: 'guest', email: this.guestEmail.trim() })
                } else if (this.accountSubStep === 'create_form') {
                    this.$emit('continue', { mode: 'create', form: this.accountForm })
                } else if (this.accountSubStep === 'signin_form') {
                    this.$emit('continue', { mode: 'signin', form: this.signInForm })
                }
            },
            handleForgotPassword() {
                this.$toast?.info?.(this.$t('pages.public.record_upload.step_account.forgot_password_toast'))
            },
        },
    }
</script>

<style scoped lang="scss">
    $brand-primary: #155dfc;
    $brand-primary-hover: #1048c7;
    $brand-border: #e2e8f0;

    .portal-card-wrapper {
        width: 100%;
    }

    .portal-card {
        background: #ffffff;
        border-radius: 16px;
        border: 1px solid $brand-border;
        box-shadow: 0 4px 20px -2px rgba(15, 23, 42, 0.05);
        overflow: hidden;

        &__plain-header {
            padding: 28px 28px 12px;
            border-bottom: 1px solid #f1f5f9;
        }

        &__body {
            padding: 28px;
        }
    }

    .card-main-title {
        font-size: 18px;
        font-weight: 700;
        color: #0f172a;
        margin: 0 0 6px;
    }

    .card-main-subtitle {
        font-size: 13px;
        color: #64748b;
        margin: 0;
        line-height: 1.45;
    }

    .account-options-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 16px;
        padding: 8px 0;
    }

    .account-option-card {
        border: 2px solid #e2e8f0;
        border-radius: 16px;
        padding: 24px 20px;
        text-align: center;
        cursor: pointer;
        background: #ffffff;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 12px;
        transition: all 0.2s ease;
        width: 100%;
        outline: none;
        font-family: inherit;

        &:hover {
            border-color: #60a5fa;
            background-color: #eff6ff;

            .option-icon-box {
                background-color: #dbeafe;
                color: #2563eb;
            }
        }

        &--recommended {
            border-color: #155dfc;
            background-color: #eff6ff;

            .option-icon-box {
                background-color: #dbeafe;
                color: #2563eb;
            }

            &:hover {
                border-color: #3b82f6;
                background-color: #dbeafe;
            }
        }

        &--selected {
            border-color: #155dfc !important;
            background-color: #eff6ff !important;
            box-shadow: 0 0 0 1px rgba(21, 93, 252, 0.2);

            .option-icon-box {
                background-color: #dbeafe !important;
                color: #155dfc !important;
            }
        }
    }

    .recommended-badge {
        position: absolute;
        top: -11px;
        background: #155dfc;
        color: #ffffff;
        font-size: 10px;
        font-weight: 700;
        padding: 3px 10px;
        border-radius: 9999px;
        letter-spacing: 0.05em;
        line-height: 1.2;
    }

    .option-icon-box {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background-color: #f1f5f9;
        color: #64748b;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: all 0.2s ease;
        flex-shrink: 0;

        .icon {
            display: flex;
            align-items: center;
            justify-content: center;
            color: inherit;
        }

        &--blue {
            background-color: #eff6ff;
            color: #155dfc;
        }
    }

    .option-content {
        display: flex;
        flex-direction: column;
        align-items: center;
    }

    .option-title {
        font-size: 14px;
        font-weight: 600;
        color: #1f2937;
        margin: 0;
        line-height: 1.3;

        &--blue {
            color: #193cb8;
        }
    }

    .option-desc {
        font-size: 12px;
        color: #64748b;
        margin: 4px 0 0;
        line-height: 1.35;
        &--blue {
            color: #155dfc;
        }
    }

    .guest-account-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .guest-info-banner {
        display: flex;
        align-items: center;
        gap: 12px;
        background-color: #f8fafc;
        border: 1px solid #e2e8f0;
        border-radius: 12px;
        padding: 12px 16px;

        .icon {
            color: #94a3b8;
            flex-shrink: 0;
        }
    }

    .guest-info-text {
        font-size: 13px;
        color: #334155;
        margin: 0;
        line-height: 1.45;

        strong {
            font-weight: 600;
            color: #0f172a;
        }
    }

    .form-label-small {
        font-size: 12px;
        font-weight: 600;
        color: #475569;
        margin-bottom: 6px;
        display: block;
    }

    .signin-account-form {
        display: flex;
        flex-direction: column;
        gap: 14px;
    }

    .forgot-password-row {
        text-align: right;
        margin: 6px 0 0;
    }

    .forgot-password-link {
        font-size: 12px;
        color: #155dfc;
        text-decoration: underline;
        cursor: pointer;

        &:hover {
            color: #1048c7;
        }
    }

    .create-account-form {
        padding-top: 4px;
    }

    .form-grid-2x2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 16px;
        margin-bottom: 16px;
    }

    .form-group-step {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }

    .form-label {
        font-size: 13px;
        font-weight: 500;
        color: #334155;
    }

    .input-box {
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 10px 14px;
        font-size: 14px;
        outline: none;
        transition: border-color 0.2s ease;

        &:focus {
            border-color: #155dfc;
        }
    }

    .terms-text {
        font-size: 12px;
        color: #64748b;
        margin: 12px 0 16px;

        a {
            color: #155dfc;
            text-decoration: underline;
        }
    }

    .back-to-options-link {
        font-size: 13px;
        color: #64748b;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 6px;

        &:hover {
            color: #0f172a;
        }
    }

    .step-nav-bar {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-top: 18px;
    }

    .nav-btn-back {
        background: #ffffff;
        border: 1px solid #cbd5e1;
        border-radius: 8px;
        padding: 10px 18px;
        font-size: 14px;
        font-weight: 600;
        color: #0f172a;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 6px;

        &:hover {
            background: #f1f5f9;
        }
    }

    .nav-btn-action {
        flex: 1;
        border-radius: 8px;
        padding: 12px 20px;
        font-size: 14px;
        font-weight: 600;
        border: none;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        transition: all 0.2s ease;

        &--active {
            background-color: #155dfc;
            color: #ffffff;

            &:hover {
                background-color: #1048c7;
            }

            .icon,
            svg {
                color: #ffffff;
                fill: #ffffff;
            }
        }

        &--disabled,
        &:disabled {
            background-color: #8da4f7;
            color: #ffffff;
            cursor: not-allowed;
            opacity: 0.9;

            .icon,
            svg {
                color: #ffffff;
                fill: #ffffff;
            }
        }
    }

    @media (max-width: 640px) {
        .account-options-grid {
            grid-template-columns: 1fr;
        }
        .form-grid-2x2 {
            grid-template-columns: 1fr;
        }
    }
</style>
